import { createRouter, createWebHistory } from 'vue-router'
import DashboardView from '../views/DashboardView.vue'
import { checkAuth } from './auth-guard'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: DashboardView
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('../views/AccountView.vue')
    },
    {
      path: '/plants',
      name: 'plants',
      component: () => import('../views/PlantsView.vue')
    },
    {
      path: '/plants/create-new',
      name: 'create-plant',
      component: () => import('../views/CreatePlantView.vue')
    },
    {
      path: '/plants/:id',
      name: 'plant-detail',
      component: () => import('../views/PlantDetailView.vue')
    },
    {
      path: '/cultivars',
      name: 'cultivars',
      component: () => import('../views/CultivarsView.vue')
    },
    {
      path: '/cultivars/:id',
      name: 'cultivar-detail',
      component: () => import('../views/CultivarDetailView.vue')
    },
    {
      path: '/favorites',
      name: 'annies-favorites',
      component: () => import('../views/AnniesFavoritesView.vue')
    },
    {
      path: '/variants',
      name: 'variants',
      component: () => import('../views/VariantsView.vue')
    },
    {
      path: '/instances',
      name: 'instances',
      component: () => import('../views/InstancesView.vue')
    },
    {
      path: '/import',
      name: 'import',
      component: () => import('../views/ImportView.vue')
    },
    {
      path: '/needs-attention',
      name: 'needs-attention',
      component: () => import('../views/PlantVerificationView.vue')
    },
    {
      path: '/enrich-names',
      name: 'enrich-names',
      component: () => import('../views/CommonNameEnrichmentView.vue')
    },
    {
      path: '/plants/:id/api-enrichment',
      name: 'plant-api-enrichment',
      component: () => import('../views/PlantAPIEnrichmentView.vue')
    },
    {
      path: '/workflow',
      name: 'workflow',
      component: () => import('../views/PlantDataWorkflow.vue')
    },
    {
      path: '/data-workflow/:id?',
      name: 'data-workflow',
      component: () => import('../views/PlantDataWorkflow.vue')
    },
    {
      path: '/scheduled-deletions',
      name: 'scheduled-deletions',
      component: () => import('../views/ScheduledDeletionsView.vue')
    },
    {
      path: '/bulk-import',
      name: 'bulk-import',
      component: () => import('../views/BulkImportView.vue')
    },
    {
      path: '/site-plan',
      name: 'site-plan',
      component: () => import('../views/SitePlanGeneratorView.vue')
    },
    {
      path: '/gis-viewer',
      name: 'gis-viewer',
      component: () => import('../views/GISLayerViewerView.vue')
    },
    {
      path: '/bulk-scraping',
      name: 'bulk-scraping',
      component: () => import('../views/BulkScrapingView.vue')
    },
    {
      path: '/fix-genus-entries',
      name: 'fix-genus-entries',
      component: () => import('../views/FixGenusEntriesView.vue')
    },
    {
      path: '/reconciliation',
      name: 'reconciliation',
      component: () => import('../views/PlantReconciliationView.vue')
    },
    {
      path: '/artwork-manager',
      name: 'artwork-manager',
      component: () => import('../views/PlantArtworkManagerView.vue')
    },
    {
      path: '/images',
      name: 'image-management',
      component: () => import('../views/ImageManagementView.vue')
    },
    {
      path: '/source-urls',
      name: 'source-url-manager',
      component: () => import('../views/SourceUrlManager.vue')
    },
    {
      path: '/nursery-pricing',
      name: 'nursery-pricing',
      component: () => import('../views/NurseryPricingView.vue')
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue')
    },
    {
      path: '/bulk-editor',
      name: 'bulk-editor',
      component: () => import('../views/BulkPlantEditorView.vue')
    },
    {
      path: '/map',
      name: 'map',
      component: () => import('../views/MapPage.vue')
    },
    {
      path: '/soil-compatibility',
      name: 'soil-compatibility',
      component: () => import('../views/SoilCompatibilityView.vue')
    },
    {
      path: '/palette-builder',
      name: 'palette-builder',
      component: () => import('../views/PaletteBuilderView.vue')
    },
    {
      path: '/image-finder',
      name: 'image-finder',
      component: () => import('../views/ImageFinderView.vue')
    },
    {
      path: '/render-colors-batch',
      name: 'render-colors-batch',
      component: () => import('../views/RenderColorBatchView.vue')
    },
    {
      path: '/db-sync',
      name: 'db-sync',
      component: () => import('../views/DbSyncView.vue')
    },
    {
      path: '/county-gis-config',
      name: 'county-gis-config',
      component: () => import('../views/CountyGisConfigView.vue')
    },
    {
      path: '/trusted-origins',
      name: 'trusted-origins',
      component: () => import('../views/TrustedOriginsView.vue')
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UsersView.vue')
    },
    {
      // Google invites now live on the Users page; keep old bookmarks working.
      path: '/google-invites',
      redirect: '/users'
    }
  ]
})

// Global navigation guard to protect all routes except login
router.beforeEach(async (to, _from, next) => {
  // Allow access to login page
  if (to.name === 'login') {
    next()
    return
  }

  // Check if user is authenticated
  const isAuthenticated = await checkAuth()

  if (!isAuthenticated) {
    // Redirect to login with return URL
    next({
      name: 'login',
      query: { next: to.fullPath }
    })
  } else {
    next()
  }
})

export default router